import React from "react";
import LegalPageLayout from "./LegalPageLayout";

const UserPrivacyPolicy: React.FC = () => {
  return (
    <LegalPageLayout title="Pheefee Privacy Policy" lastUpdated="April 20, 2026">
      <p>
        Pheefee Technologies Inc. (&ldquo;Pheefee,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
        &ldquo;our&rdquo;) respects your privacy. This Privacy Policy explains how we collect, use,
        disclose, retain, and otherwise process personal information when you visit our website,
        create an account, use our platform, communicate with us, book or provide services,
        participate in recorded sessions, or otherwise interact with us online or offline.
      </p>
      <p>
        This Privacy Policy applies to all platform users, including dog owners, customers,
        visitors, trainers, applicants, and other individuals who interact with Pheefee
        (collectively, &ldquo;Users,&rdquo; &ldquo;you,&rdquo; or &ldquo;your&rdquo;).
      </p>
      <p>
        By accessing or using the platform, you acknowledge that you have read and understood this
        Privacy Policy.
      </p>

      <h2>1. Scope and Role of the Platform</h2>
      <p>
        Pheefee operates an online platform that helps dog owners and other users locate, connect
        with, pay, and book remote dog-training related services from independent trainers. Pheefee
        does not provide dog training, veterinary, medical, or emergency services. Trainers are
        independent providers, and their services are subject to separate agreements and
        interactions with Users.
      </p>

      <h2>2. Personal Information We Collect</h2>
      <p>
        We may collect the following categories of personal information, depending on how you
        interact with the platform.
      </p>
      <h3>2.1 Information You Provide Directly</h3>
      <p>
        We may collect information you provide when you create an account, complete a profile,
        book or provide services, contact us, or otherwise use the platform, including:
      </p>
      <ul>
        <li>name</li>
        <li>email address</li>
        <li>phone number</li>
        <li>account login credentials</li>
        <li>profile information</li>
        <li>billing information and payment-related details</li>
        <li>communications you send through the platform</li>
        <li>reviews, feedback, photos, videos, and other content you upload</li>
        <li>
          information you provide about your dog, including behavioral concerns, training goals,
          history, and related details
        </li>
        <li>
          trainer onboarding information, including credentials, certifications, experience,
          resume, or CV details, insurance information, and background-check-related information.
        </li>
      </ul>
      <h3>2.2 Information Collected Automatically</h3>
      <p>
        When you use the platform, we may automatically collect certain technical and usage
        information, such as:
      </p>
      <ul>
        <li>IP address</li>
        <li>device type</li>
        <li>browser type</li>
        <li>operating system</li>
        <li>log data</li>
        <li>approximate location derived from IP address</li>
        <li>pages viewed</li>
        <li>session activity</li>
        <li>timestamps and referral data</li>
        <li>platform interactions and performance data</li>
      </ul>
      <h3>2.3 Communications, Recordings, and Session Data</h3>
      <p>We may collect and store:</p>
      <ul>
        <li>messages sent through the platform</li>
        <li>session metadata</li>
        <li>session summaries</li>
        <li>
          audio, video, and transcript records of sessions or communications where recording,
          monitoring, or transcription occurs through the platform or its systems
        </li>
        <li>
          trust and safety reports, complaint information, and related investigative records
        </li>
      </ul>
      <h3>2.4 Information from Third Parties</h3>
      <p>We may receive information from third parties, including:</p>
      <ul>
        <li>payment processors</li>
        <li>identity verification and background check providers</li>
        <li>hosting and infrastructure providers</li>
        <li>customer support and communications vendors</li>
        <li>analytics providers</li>
        <li>business partners or referral sources</li>
        <li>law enforcement, regulators, or other parties where legally required or appropriate</li>
      </ul>

      <h2>3. How We Collect Personal Information</h2>
      <p>We collect personal information:</p>
      <ul>
        <li>directly from you</li>
        <li>automatically through your use of the platform</li>
        <li>from cookies and similar technologies, where used</li>
        <li>from service providers and vendors supporting our operations</li>
        <li>from trainers, users, or third parties involved in a booking, complaint, or safety matter</li>
        <li>from screening providers in connection with trainer onboarding or ongoing eligibility</li>
      </ul>

      <h2>4. How We Use Personal Information</h2>
      <p>We may use personal information for the following business and commercial purposes:</p>
      <ul>
        <li>to create and manage accounts</li>
        <li>to operate, maintain, and improve the platform</li>
        <li>to facilitate bookings, communications, and payments</li>
        <li>to process transactions and send related notices</li>
        <li>to provide customer support</li>
        <li>to verify identity, qualifications, insurance, and eligibility</li>
        <li>to conduct or facilitate trainer screening and onboarding</li>
        <li>to review complaints, disputes, chargebacks, and trust-and-safety issues</li>
        <li>
          to monitor, record, transcribe, summarize, review, and audit platform interactions for
          quality assurance, training, trust and safety, policy enforcement, legal compliance,
          dispute resolution, and product improvement
        </li>
        <li>to enforce our terms, policies, and platform rules</li>
        <li>to protect the security, integrity, and functionality of the platform</li>
        <li>
          to detect, investigate, and prevent fraud, abuse, unauthorized activity, and other
          harmful conduct
        </li>
        <li>to comply with legal, regulatory, contractual, and law enforcement obligations</li>
        <li>
          to communicate with you about your account, bookings, platform updates, support matters,
          and other service-related topics
        </li>
        <li>to send marketing or promotional communications where permitted by law</li>
        <li>to conduct analytics, research, internal reporting, and performance measurement</li>
        <li>
          to support corporate transactions such as financing, acquisition, merger, sale,
          restructuring, or similar events
        </li>
      </ul>

      <h2>5. Cookies, Analytics, and Advertising Technologies</h2>
      <p>
        We may use cookies, pixels, SDKs, local storage, and similar technologies to operate the
        platform, remember preferences, understand usage patterns, improve performance, and support
        security and analytics.
      </p>
      <p>
        At present, Pheefee may use limited website technologies and service-provider tools to
        support platform functionality, analytics, and basic measurement. In the future, Pheefee
        may also use additional analytics, cookies, pixels, or advertising technologies, including
        tools used for campaign measurement, remarketing, or similar advertising purposes. If we do
        so, we will update this Privacy Policy and provide any additional notice, consent, or
        opt-out choices required by applicable law before using those tools in a manner that
        triggers such obligations.
      </p>
      <p>
        Depending on your jurisdiction and our actual practices at the time, you may have rights to
        opt out of certain advertising-related uses or disclosures of personal information.
      </p>

      <h2>6. How We Disclose Personal Information</h2>
      <p>
        We may disclose personal information to the following categories of recipients, as
        reasonably necessary for the purposes described above:
      </p>
      <ul>
        <li>trainers and users involved in a booking or communication</li>
        <li>payment processors and financial service providers</li>
        <li>identity verification and background check providers</li>
        <li>cloud hosting, storage, communications, and customer support vendors</li>
        <li>security, fraud prevention, analytics, and technical service providers</li>
        <li>insurers, auditors, legal advisors, and professional service providers</li>
        <li>affiliates and entities involved in corporate transactions</li>
        <li>
          government agencies, regulators, courts, law enforcement, or other third parties where
          required or permitted by law
        </li>
      </ul>
      <p>
        We may also disclose information if we believe disclosure is necessary to protect the
        rights, property, safety, security, or integrity of Pheefee, our users, trainers, the
        public, or the platform.
      </p>
      <p>
        We do not state in this Privacy Policy that we currently &ldquo;sell&rdquo; or
        &ldquo;share&rdquo; personal information for cross-context behavioral advertising unless
        and until our actual practices support that statement. If our practices change, we will
        revise this Privacy Policy and provide any required rights mechanisms.
      </p>

      <h2>7. Trainer Screening and Verification</h2>
      <p>
        For trainers, we may collect and process personal information needed to verify identity,
        review credentials, evaluate eligibility, conduct background screening, review insurance,
        and monitor continuing compliance with platform requirements. Such information may come
        from the trainer, third-party providers, or follow-up reviews conducted during the
        trainer&rsquo;s relationship with the platform.
      </p>

      <h2>8. Recordings, Transcripts, and Summaries</h2>
      <p>
        Pheefee may monitor, record, transcribe, store, review, audit, and use platform
        communications, video sessions, messages, summaries, and related records for quality
        assurance, complaint review, trust and safety, training, policy enforcement, legal
        compliance, dispute resolution, product improvement, and other legitimate business
        purposes.
      </p>
      <p>
        Where required by applicable law, we will seek consent or provide notice regarding
        recording and monitoring practices through the platform flow, onboarding materials, booking
        flow, session interface, or other appropriate mechanisms.
      </p>

      <h2>9. Data Retention</h2>
      <p>
        We retain personal information for as long as reasonably necessary to fulfill the purposes
        described in this Privacy Policy, including to:
      </p>
      <ul>
        <li>maintain your account</li>
        <li>provide the platform and related services</li>
        <li>complete bookings and payments</li>
        <li>preserve business and transaction records</li>
        <li>resolve disputes and enforce agreements</li>
        <li>investigate complaints and trust-and-safety matters</li>
        <li>comply with legal, tax, accounting, insurance, and regulatory obligations</li>
        <li>protect against fraud, abuse, and legal claims</li>
      </ul>
      <p>
        Retention periods may vary depending on the type of data, the sensitivity of the data, the
        nature of the relationship, and our legal or operational needs. When we no longer need
        personal information, we may delete, de-identify, anonymize, or otherwise dispose of it in
        accordance with applicable law and our internal practices.
      </p>

      <h2>10. Your Rights and Choices</h2>
      <p>
        Depending on where you live, you may have rights regarding your personal information,
        which may include the right to:
      </p>
      <ul>
        <li>know what personal information we collect, use, disclose, or process</li>
        <li>access or obtain a copy of certain personal information</li>
        <li>request correction of inaccurate personal information</li>
        <li>request deletion of certain personal information</li>
        <li>
          opt out of certain uses of personal information, including where applicable certain
          sales, sharing, or targeted advertising activities
        </li>
        <li>limit certain uses of sensitive personal information where applicable</li>
        <li>appeal a denial of a privacy request, where required by law</li>
        <li>not be discriminated against for exercising applicable privacy rights</li>
      </ul>
      <p>
        You may also opt out of marketing emails by using the unsubscribe link in those messages.
        We may still send non-promotional communications related to your account, bookings,
        transactions, security, or support.
      </p>
      <p>To exercise privacy rights, contact us at:</p>
      <p>
        <a href="mailto:pheefeeplatform@gmail.com">pheefeeplatform@gmail.com</a>
      </p>
      <p>
        We may need to verify your identity before processing certain requests. You may also use
        an authorized agent where permitted by law.
      </p>

      <h2>11. California Privacy Notice</h2>
      <p>
        This Section applies to California residents and supplements the rest of this Privacy
        Policy. California residents have rights under the California Consumer Privacy Act, as
        amended by the California Privacy Rights Act, including rights to know, access, correct,
        delete, and opt out in certain circumstances. The CCPA regulations also require businesses
        to explain categories collected, sources, purposes, disclosures, and how rights may be
        exercised.
      </p>
      <h3>11.1 Categories of Personal Information Collected</h3>
      <p>
        In the preceding 12 months, we may have collected the following categories of personal
        information, depending on the user&rsquo;s interaction with the platform:
      </p>
      <ul>
        <li>
          identifiers, such as name, email address, phone number, account name, online identifiers,
          and IP address
        </li>
        <li>
          customer records information, such as billing information and records relating to
          services booked or provided
        </li>
        <li>
          commercial information, such as booking history, payment history, and related transaction
          information
        </li>
        <li>
          internet or electronic network activity information, such as platform usage and device
          interaction data
        </li>
        <li>geolocation data, such as approximate location derived from IP address</li>
        <li>
          audio, visual, or similar information, such as recorded sessions, images, videos, and
          transcripts
        </li>
        <li>
          professional or employment-related information, such as trainer credentials, experience,
          r&eacute;sum&eacute; or CV details, and insurance information
        </li>
        <li>
          sensitive personal information, to the extent collected, such as account credentials or
          limited information gathered in trainer screening or payment processes
        </li>
      </ul>
      <h3>11.2 Sources</h3>
      <p>We collect these categories of personal information from:</p>
      <ul>
        <li>you directly</li>
        <li>your use of the platform</li>
        <li>service providers and vendors</li>
        <li>background check or verification providers</li>
        <li>other users involved in a booking or complaint</li>
        <li>automated technologies such as cookies or similar tools, where used</li>
      </ul>
      <h3>11.3 Purposes</h3>
      <p>
        We collect and use personal information for the business and commercial purposes described
        in Section 4.
      </p>
      <h3>11.4 Disclosures</h3>
      <p>
        We may disclose the categories above to the recipient categories described in Section 6
        for business purposes.
      </p>
      <h3>11.5 Sales, Sharing, and Sensitive Personal Information</h3>
      <p>
        We do not sell or share personal information for cross-context behavioral advertising. If
        our practices change, we will update this Privacy Policy and provide any required notice
        and opt-out mechanisms.
      </p>
      <p>
        We use sensitive personal information only for permitted business purposes and not to
        infer characteristics about individuals except as allowed by law and necessary for our
        operations.
      </p>
      <h3>11.6 California Rights Requests</h3>
      <p>
        California residents may submit requests to know, access, correct, or delete personal
        information by contacting us using the contact information in Section 10. California
        residents may also have rights to opt out of sale or sharing and to limit certain uses of
        sensitive personal information if and when those rights apply to our actual practices.
      </p>
      <p>We will not unlawfully discriminate against you for exercising applicable privacy rights.</p>

      <h2>12. Security</h2>
      <p>
        We use reasonable administrative, technical, and physical safeguards designed to protect
        personal information appropriate to the nature of the information and our operations. No
        method of transmission or storage is completely secure, and we cannot guarantee absolute
        security. The FTC advises businesses to map the data they collect, limit access, secure
        storage and transmission, and align security measures to the sensitivity of the data.
      </p>

      <h2>13. Children&rsquo;s Privacy</h2>
      <p>
        The platform is not directed to children, and we do not intend to collect personal
        information directly from children under 13 through the platform. If we learn that we
        collected personal information from a child in a manner that requires deletion under
        applicable law, we will take reasonable steps to address it.
      </p>

      <h2>14. Third-Party Services</h2>
      <p>
        The platform may rely on or link to third-party services, websites, payment providers,
        communications providers, or other vendors. We are not responsible for the privacy
        practices of third parties except as described in this Privacy Policy with respect to our
        own processing and disclosures.
      </p>

      <h2>15. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. The updated version will be effective
        when posted, unless a later effective date is stated. Your continued use of the platform
        after an updated Privacy Policy becomes effective constitutes your acknowledgment of the
        updated policy to the extent permitted by law.
      </p>

      <h2>16. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or want to exercise your privacy rights,
        contact us at:
      </p>
      <p>
        <strong>Pheefee Technologies Inc.</strong>
        <br />
        <a href="mailto:pheefeeplatform@gmail.com">pheefeeplatform@gmail.com</a>
      </p>
    </LegalPageLayout>
  );
};

export default UserPrivacyPolicy;
